/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type Specification, type ProductShelfResultsModel } from '@/api/types/browse-search-types';
import { type ProductShelfResponse, type ProductShelfACESResponse } from '@/features/category';

const mapShelfResultData = (
  attributes: Array<{
    vehicleProductAttributeName: string | null;
    vehicleProductAttributeValue: string | null;
    vehicleProductAttributeValueNote: string | null;
  }>,
) => {
  return attributes.map((attribute) => ({
    attribute: attribute.vehicleProductAttributeName,
    value: attribute.vehicleProductAttributeValue,
    description: attribute.vehicleProductAttributeValueNote,
  }));
};

export const mapPartsProProductShelfResultsAttributes = (
  productShelfData?: ProductShelfResponse | ProductShelfACESResponse,
  isACESEnabled: boolean = false,
) => {
  if (!productShelfData) {
    return [];
  } else if ('productShelfResults' in productShelfData && !isACESEnabled) {
    return Array.isArray(
      (productShelfData.productShelfResults as ProductShelfResultsModel)?.vehicleProductAttributes,
    )
      ? mapShelfResultData(
          (productShelfData.productShelfResults as ProductShelfResultsModel)
            ?.vehicleProductAttributes ?? [],
        )
      : [];
  }

  return [];
};

/**
 * Maps specification attributes from ACES product shelf/search responses.
 *
 * - Only includes specs where:
 *   - vehicleFit === 'FITS'
 *   - attribute key is not "diagram"
 *   - attributes object is non-empty
 * - Deduplicates attributes:
 *   - If the same attribute appears with different values → excluded entirely
 *   - Keeps only one entry per attribute
 * - Limits output to top 3 attributes
 *
 * This function is shared across:
 * - Product shelf (ACES)
 * - Search product finder
 */
export const mapProductSpecificationAttributes = (specifications?: Specification[]) => {
  const fitsSpecs = specifications
    ?.filter((specification) => {
      const { attributes } = specification;

      if (!attributes || Object.keys(attributes).length === 0) {
        return false;
      }

      const key = Object.keys(attributes)[0];

      return specification.vehicleFit === 'FITS' && key?.toLowerCase() !== 'diagram';
    })
    ?.map((specification) => {
      const attributes = specification.attributes as NonNullable<typeof specification.attributes>;
      const attribute = Object.keys(attributes)[0];
      const value = attributes[attribute];

      return {
        attribute,
        value,
        description: null, // unused
      };
    });

  if (!fitsSpecs?.length) {
    return [];
  }

  const attributeValueMap = new Map<string, string>();
  const duplicateAttributes = new Set<string>();

  for (const spec of fitsSpecs) {
    if (!spec.attribute) {
      continue;
    }

    if (attributeValueMap.has(spec.attribute)) {
      if (attributeValueMap.get(spec.attribute) !== spec.value) {
        duplicateAttributes.add(spec.attribute);
      }
    } else {
      attributeValueMap.set(spec.attribute, spec.value ?? '');
    }
  }

  return fitsSpecs
    .filter((spec) => spec.attribute && !duplicateAttributes.has(spec.attribute))
    .filter((spec, index, arr) => arr.findIndex((s) => s.attribute === spec.attribute) === index)
    .slice(0, 3);
};
